import { inject, Injectable } from '@angular/core';
import { NativeDateAdapter } from '@angular/material/core';
import { LanguageService } from '../../i18n/language.service';

/**
 * Date adapter that follows the app's selected language.
 *
 * It subscribes to LanguageService.currentLang$ and updates its locale on every
 * change, so all Material datepickers display/parse dates in the order expected
 * by the active language (en → MM/DD/YYYY, pt-BR/pt-PT/es → DD/MM/YYYY).
 */
@Injectable()
export class BrNativeDateAdapter extends NativeDateAdapter {
  private readonly languageService = inject(LanguageService);

  // Field initializer runs after super(): keep the adapter locale in sync with
  // the selected language (fires immediately with the current value).
  private readonly _localeSync = this.languageService.currentLang$.subscribe((lang) =>
    this.setLocale(lang),
  );

  private usesMonthFirst(): boolean {
    return String(this.locale ?? '').toLowerCase().startsWith('en');
  }

  override parse(value: unknown): Date | null {
    if (typeof value === 'string') {
      const v = value.trim();
      if (v === '') return null;

      const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(v);
      if (!m) return new Date(NaN);

      const monthFirst = this.usesMonthFirst();
      const dd = monthFirst ? +m[2] : +m[1];
      const mm = monthFirst ? +m[1] : +m[2];
      const yyyy = +m[3];
      const d = new Date(yyyy, mm - 1, dd);
      const ok = d.getFullYear() === yyyy && d.getMonth() === mm - 1 && d.getDate() === dd;
      return ok ? d : new Date(NaN);
    }
    if (value instanceof Date) return isNaN(value.getTime()) ? new Date(NaN) : value;
    return new Date(NaN);
  }

  override format(date: Date): string {
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yyyy = date.getFullYear();
    return this.usesMonthFirst() ? `${mm}/${dd}/${yyyy}` : `${dd}/${mm}/${yyyy}`;
  }
}
